import React, { useState } from "react";
import { post } from "../../../services/smartApiService";
import * as XLSX from "xlsx";

interface BulkFinancialUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface ExcelRow {
  symbol: string;
  quarter: string;
  year: number;
  [key: string]: any;
}

const BulkFinancialUploadModal: React.FC<BulkFinancialUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Validate file type
      const fileExt = selectedFile.name.split(".").pop()?.toLowerCase();
      if (fileExt !== "xlsx" && fileExt !== "xls") {
        setError("Please upload a valid Excel file (.xlsx or .xls)");
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
      setUploadResult(null);
    }
  };

  const downloadTemplate = () => {
    // Create sample template data
    const templateData = [
      {
        symbol: "RELIANCE",
        quarter: "Q4",
        year: 2024,
        quarter_end_date: "2024-03-31",
        eps: 88.5,
        eps_growth: 12.3,
        revenue: 245000,
        revenue_growth_yoy: 18.5,
        net_profit: 18500,
        net_profit_margin: 7.55,
        operating_profit_margin: 9.2,
        gross_profit_margin: 12.5,
        roe: 9.8,
        roce: 11.2,
        roa: 5.6,
        pe_ratio: 23.5,
        pb_ratio: 2.1,
        price_to_sales: 1.8,
        ev_to_ebitda: 12.5,
        total_assets: 1250000,
        total_liabilities: 450000,
        shareholders_equity: 800000,
        book_value_per_share: 1250,
        debt_to_equity: 0.56,
        debt_to_assets: 0.36,
        interest_coverage: 8.5,
        current_ratio: 1.2,
        quick_ratio: 0.9,
        operating_cash_flow: 85000,
        free_cash_flow: 62000,
        cash_flow_per_share: 97,
        order_book: 150000,
        data_source: "Screener.in",
      },
      {
        symbol: "TCS",
        quarter: "Q4",
        year: 2024,
        quarter_end_date: "2024-03-31",
        eps: 125.5,
        eps_growth: 15.2,
        revenue: 62000,
        revenue_growth_yoy: 13.5,
        net_profit: 12500,
        net_profit_margin: 20.16,
        operating_profit_margin: 25.5,
        gross_profit_margin: 28.2,
        roe: 45.2,
        roce: 52.8,
        roa: 28.5,
        pe_ratio: 28.5,
        pb_ratio: 12.5,
        price_to_sales: 7.2,
        ev_to_ebitda: 22.5,
        total_assets: 125000,
        total_liabilities: 45000,
        shareholders_equity: 80000,
        book_value_per_share: 285,
        debt_to_equity: 0.15,
        debt_to_assets: 0.12,
        interest_coverage: 45.2,
        current_ratio: 2.5,
        quick_ratio: 2.3,
        operating_cash_flow: 18500,
        free_cash_flow: 16500,
        cash_flow_per_share: 58,
        order_book: null,
        data_source: "Screener.in",
      },
    ];

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(templateData);

    // Set column widths
    ws["!cols"] = [
      { wch: 15 }, // symbol
      { wch: 10 }, // quarter
      { wch: 10 }, // year
      { wch: 18 }, // quarter_end_date
      { wch: 12 }, // eps
      { wch: 15 }, // eps_growth
      { wch: 15 }, // revenue
      { wch: 20 }, // revenue_growth_yoy
      { wch: 15 }, // net_profit
      { wch: 20 }, // net_profit_margin
      { wch: 25 }, // operating_profit_margin
      { wch: 22 }, // gross_profit_margin
      { wch: 10 }, // roe
      { wch: 10 }, // roce
      { wch: 10 }, // roa
      { wch: 12 }, // pe_ratio
      { wch: 12 }, // pb_ratio
      { wch: 18 }, // price_to_sales
      { wch: 15 }, // ev_to_ebitda
      { wch: 15 }, // total_assets
      { wch: 18 }, // total_liabilities
      { wch: 20 }, // shareholders_equity
      { wch: 22 }, // book_value_per_share
      { wch: 18 }, // debt_to_equity
      { wch: 18 }, // debt_to_assets
      { wch: 18 }, // interest_coverage
      { wch: 15 }, // current_ratio
      { wch: 15 }, // quick_ratio
      { wch: 22 }, // operating_cash_flow
      { wch: 18 }, // free_cash_flow
      { wch: 22 }, // cash_flow_per_share
      { wch: 15 }, // order_book
      { wch: 18 }, // data_source
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Financial Data");

    // Add instructions sheet
    const instructions = [
      {
        Field: "symbol",
        Description: "Stock ticker/symbol (REQUIRED)",
        Example: "RELIANCE, TCS, INFY",
        Type: "Text",
      },
      {
        Field: "quarter",
        Description: "Quarter (REQUIRED)",
        Example: "Q1, Q2, Q3, Q4",
        Type: "Text",
      },
      {
        Field: "year",
        Description: "Financial Year (REQUIRED)",
        Example: "2024, 2025",
        Type: "Number",
      },
      {
        Field: "quarter_end_date",
        Description: "Quarter end date (OPTIONAL)",
        Example: "2024-03-31",
        Type: "Date (YYYY-MM-DD)",
      },
      {
        Field: "eps",
        Description: "Earnings Per Share in ₹",
        Example: "88.5",
        Type: "Number",
      },
      {
        Field: "eps_growth",
        Description: "EPS Growth %",
        Example: "12.3",
        Type: "Number",
      },
      {
        Field: "revenue",
        Description: "Revenue in Crores",
        Example: "245000",
        Type: "Number",
      },
      {
        Field: "revenue_growth_yoy",
        Description: "Revenue Growth Year-over-Year %",
        Example: "18.5",
        Type: "Number",
      },
      {
        Field: "net_profit",
        Description: "Net Profit in Crores",
        Example: "18500",
        Type: "Number",
      },
      {
        Field: "net_profit_margin",
        Description: "Net Profit Margin %",
        Example: "7.55",
        Type: "Number",
      },
      {
        Field: "roe",
        Description: "Return on Equity %",
        Example: "9.8",
        Type: "Number",
      },
      {
        Field: "roce",
        Description: "Return on Capital Employed %",
        Example: "11.2",
        Type: "Number",
      },
      {
        Field: "pe_ratio",
        Description: "Price to Earnings Ratio",
        Example: "23.5",
        Type: "Number",
      },
      {
        Field: "pb_ratio",
        Description: "Price to Book Ratio",
        Example: "2.1",
        Type: "Number",
      },
      {
        Field: "debt_to_equity",
        Description: "Debt to Equity Ratio",
        Example: "0.56",
        Type: "Number",
      },
      {
        Field: "data_source",
        Description: "Data source name",
        Example: "Screener.in, Manual, BSE, NSE",
        Type: "Text",
      },
    ];

    const wsInstructions = XLSX.utils.json_to_sheet(instructions);
    wsInstructions["!cols"] = [
      { wch: 25 },
      { wch: 40 },
      { wch: 30 },
      { wch: 20 },
    ];
    XLSX.utils.book_append_sheet(wb, wsInstructions, "Instructions");

    // Download file
    XLSX.writeFile(wb, "financial_data_template.xlsx");
  };

  const parseExcelFile = (file: File): Promise<ExcelRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          const data = e.target?.result;
          const workbook = XLSX.read(data, { type: "binary" });

          // Get first sheet
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert to JSON
          const jsonData: ExcelRow[] = XLSX.utils.sheet_to_json(worksheet, {
            raw: false,
            defval: null,
          });

          resolve(jsonData);
        } catch (error) {
          reject(new Error("Failed to parse Excel file"));
        }
      };

      reader.onerror = () => {
        reject(new Error("Failed to read file"));
      };

      reader.readAsBinaryString(file);
    });
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file to upload");
      return;
    }

    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      // Parse Excel file
      const excelData = await parseExcelFile(file);

      if (excelData.length === 0) {
        setError("Excel file is empty");
        setUploading(false);
        return;
      }

      // Validate required fields
      const invalidRows: number[] = [];
      excelData.forEach((row, index) => {
        if (!row.symbol || !row.quarter || !row.year) {
          invalidRows.push(index + 2); // +2 for header and 0-based index
        }
      });

      if (invalidRows.length > 0) {
        setError(
          `Missing required fields (symbol, quarter, year) in rows: ${invalidRows.join(", ")}`
        );
        setUploading(false);
        return;
      }

      // Send to backend
      post("/api/companies/financials/bulk-upload", { data: excelData }).subscribe({
        next: (response: any) => {
          setUploadResult(response.data);
          setUploading(false);

          if (response.data.success_count > 0) {
            setTimeout(() => {
              onSuccess();
            }, 2000);
          }
        },
        error: (err) => {
          console.error("Upload failed:", err);
          setError(err?.error?.message || "Failed to upload financial data");
          setUploading(false);
        },
      });
    } catch (err: any) {
      setError(err.message || "Failed to process Excel file");
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setUploadResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal is-active">
      <div className="modal-background" onClick={handleClose}></div>
      <div className="modal-card" style={{ width: "600px" }}>
        <header className="modal-card-head">
          <p className="modal-card-title">
            <span className="icon-text">
              <span className="icon">
                <i className="fas fa-file-excel"></i>
              </span>
              <span>Bulk Upload Financial Data</span>
            </span>
          </p>
          <button className="delete" aria-label="close" onClick={handleClose}></button>
        </header>

        <section className="modal-card-body">
          {!uploadResult ? (
            <>
              <div className="notification is-info is-light">
                <p className="has-text-weight-semibold mb-2">Instructions:</p>
                <ul style={{ marginLeft: "1.5rem" }}>
                  <li>Download the template Excel file below</li>
                  <li>Fill in financial data from Screener.in or other sources</li>
                  <li>
                    Required fields: <strong>symbol</strong>, <strong>quarter</strong>,{" "}
                    <strong>year</strong>
                  </li>
                  <li>Upload the completed Excel file</li>
                  <li>System will auto-calculate derived metrics and signals</li>
                </ul>
              </div>

              <div className="field">
                <label className="label">1. Download Template</label>
                <div className="control">
                  <button className="button is-link is-light" onClick={downloadTemplate}>
                    <span className="icon">
                      <i className="fas fa-download"></i>
                    </span>
                    <span>Download Excel Template</span>
                  </button>
                </div>
                <p className="help">
                  Contains sample data and field descriptions for financial metrics
                </p>
              </div>

              <hr />

              <div className="field">
                <label className="label">2. Upload Completed Excel File</label>
                <div className="file has-name is-fullwidth">
                  <label className="file-label">
                    <input
                      className="file-input"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                    />
                    <span className="file-cta">
                      <span className="file-icon">
                        <i className="fas fa-upload"></i>
                      </span>
                      <span className="file-label">Choose Excel file…</span>
                    </span>
                    {file && (
                      <span className="file-name">{file.name}</span>
                    )}
                  </label>
                </div>
                <p className="help">Supported formats: .xlsx, .xls</p>
              </div>

              {error && (
                <div className="notification is-danger is-light">
                  <button className="delete" onClick={() => setError(null)}></button>
                  {error}
                </div>
              )}
            </>
          ) : (
            <div className="content">
              <div
                className={`notification ${
                  uploadResult.failed_count === 0 ? "is-success" : "is-warning"
                } is-light`}
              >
                <h5 className="title is-5">Upload Complete</h5>
                <p>
                  <strong>Successfully processed:</strong> {uploadResult.success_count} companies
                </p>
                <p>
                  <strong>Failed:</strong> {uploadResult.failed_count} companies
                </p>
              </div>

              {uploadResult.results && uploadResult.results.length > 0 && (
                <div className="box" style={{ maxHeight: "300px", overflowY: "auto" }}>
                  <p className="has-text-weight-semibold mb-3">Details:</p>
                  <table className="table is-fullwidth is-size-7">
                    <thead>
                      <tr>
                        <th>Symbol</th>
                        <th>Quarter</th>
                        <th>Status</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {uploadResult.results.map((result: any, index: number) => (
                        <tr key={index}>
                          <td>
                            <strong>{result.symbol}</strong>
                          </td>
                          <td>
                            {result.quarter} {result.year}
                          </td>
                          <td>
                            <span
                              className={`tag is-small ${
                                result.success ? "is-success" : "is-danger"
                              }`}
                            >
                              {result.success ? "Success" : "Failed"}
                            </span>
                          </td>
                          <td className="is-size-7">{result.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>

        <footer className="modal-card-foot">
          {!uploadResult ? (
            <>
              <button
                className={`button is-primary ${uploading ? "is-loading" : ""}`}
                onClick={handleUpload}
                disabled={!file || uploading}
              >
                <span className="icon">
                  <i className="fas fa-cloud-upload-alt"></i>
                </span>
                <span>Upload & Process</span>
              </button>
              <button className="button" onClick={handleClose} disabled={uploading}>
                Cancel
              </button>
            </>
          ) : (
            <button className="button is-success" onClick={handleClose}>
              Done
            </button>
          )}
        </footer>
      </div>
    </div>
  );
};

export default BulkFinancialUploadModal;
